import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { config } from "../Environment";
import useRetryFetch from "../hooks/useRetryFetch";
import useAuth from "../hooks/useAuth";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { trackContact } from "../lib/analytics";
import { trackContactWithData } from "../lib/appTracking";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { Spinner } from "./ui/spinner";

const contactCategories = [
  { value: "general", label: "general_inquiry" },
  { value: "technical", label: "technical_support" },
  { value: "sales", label: "sales_pricing" },
  { value: "partnership", label: "partnership_opportunity" },
  { value: "bug", label: "bug_report" },
  { value: "feature", label: "feature_request" },
  { value: "account", label: "account_issue" },
  { value: "activity_request", label: "activity_program_request" },
  { value: "interest_expert", label: "interest_to_be_expert" },
  { value: "interest_technology", label: "interest_to_use_technology" },
  { value: "contact_me", label: "please_contact_me" },
  { value: "other", label: "other" },
];

function ContactForm() {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const { auth } = useAuth();
  const location = useLocation();

  // Check for category in URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const categoryParam = queryParams.get('category');

  const [formData, setFormData] = useState({
    name: "",
    email: auth?.email || "",
    category: categoryParam || "",
    company: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear messages when user types
    setSuccessMsg("");
    setErrorMsg("");
  };

  const handleCategoryChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      category: value,
    }));
    setSuccessMsg("");
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccessMsg("");
    setErrorMsg("");

    try {
      const response = await retryFetch(`${config.API_BASE}/api/contact/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMsg(data.message || t("contact_form_success") || "Message sent successfully!");

        // Track contact form submission
        // GA4 - privacy-safe (no personal data)
        trackContact();

        // Internal tracking - with user details
        trackContactWithData({
          userEmail: formData.email,
          method: "contact_form",
          ...formData
        });

        // Reset form
        setFormData({
          name: "",
          email: auth?.email || "",
          category: "",
          company: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        setErrorMsg(data.message || t("contact_form_error") || "Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      setErrorMsg(t("contact_form_error") || "Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border bg-background p-5">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="name">
              {t("name") || "Name"} <span className="text-destructive" aria-hidden="true">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              placeholder={t("your_full_name") || "Your name"}
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className="h-10"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">
              {t("email") || "Email"} <span className="text-destructive" aria-hidden="true">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className="h-10"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="category">
              {t("whats_this_about") || "What's this about?"} <span className="text-destructive" aria-hidden="true">*</span>
            </Label>
            <Select value={formData.category} onValueChange={handleCategoryChange} required disabled={isSubmitting}>
              <SelectTrigger id="category">
                <SelectValue placeholder={t("pick_a_topic") || "Pick a topic…"} />
              </SelectTrigger>
              <SelectContent>
                {contactCategories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {t(cat.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="company">
              {t("company") || "Company"}{" "}
              <span className="text-muted-foreground font-normal text-xs">({t("optional") || "optional"})</span>
            </Label>
            <Input
              id="company"
              name="company"
              type="text"
              autoComplete="organization"
              placeholder={t("company_name") || "Company or organization"}
              value={formData.company}
              onChange={handleChange}
              disabled={isSubmitting}
              className="h-10"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="message">
            {t("message") || "Message"} <span className="text-destructive" aria-hidden="true">*</span>
          </Label>
          <Textarea
            id="message"
            name="message"
            placeholder={t("message_placeholder") || "Tell us what's on your mind — the more detail, the better we can help."}
            value={formData.message}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            rows={5}
            className="resize-none"
          />
        </div>

        {successMsg && (
          <div
            role="status"
            aria-live="polite"
            className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-800"
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {errorMsg && (
          <div
            role="alert"
            aria-live="polite"
            className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-3 text-sm text-destructive"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        <Button
          type="submit"
          disabled={isSubmitting || !formData.category}
          className="w-full h-10"
          style={{ touchAction: "manipulation" }}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" />
              {t("sending") || "Sending…"}
            </span>
          ) : (
            t("send_message") || "Send message"
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          {t("contact_form_note") || "We read every message and typically reply within 1–2 business days."}
        </p>
      </form>
    </div>
  );
}

export default ContactForm;
